import { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress, Alert, Chip, Tooltip,
} from '@mui/material';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { toast } from 'react-toastify';
import warrantyTicketServices from '../services/warrantyTicketServices';
import propertiesServices from '../services/propertiesServices';

/**
 * Photo picker for the *create* flow, used before a warranty ticket exists.
 *
 * It lists the property's photos (PropertyMedia) and lets the technician
 * pick the equipment-label shots while still filling in the model / serial.
 * The selected media ids are lifted to the parent (controlled `value` +
 * `onChange`); the parent attaches them to the ticket right after it is
 * created.
 */
export default function WarrantyTicketPhotoPicker({
  propertyId,
  ccProjectUrl,
  value = [],
  onChange,
  dense = false,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  const selected = new Set(value);

  const load = useCallback(async () => {
    if (!propertyId) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const res = await warrantyTicketServices.listPropertyAvailablePhotos(propertyId);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load property photos', e);
    }
    setLoading(false);
  }, [propertyId]);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = async () => {
    if (!propertyId) return;
    setPulling(true);
    try {
      // Pull just-taken photos from CompanyCam into the gallery first, then
      // reload. If the pull endpoint is unavailable we still reload.
      await propertiesServices.pullCcPhotos(propertyId).catch(() => {});
    } finally {
      await load();
      setPulling(false);
    }
  };

  const togglePick = (mediaId) => {
    const next = new Set(selected);
    if (next.has(mediaId)) next.delete(mediaId);
    else next.add(mediaId);
    onChange?.(Array.from(next));
  };

  const removeSelected = (mediaId) => {
    onChange?.(value.filter((id) => id !== mediaId));
  };

  const selectedItems = items.filter((m) => selected.has(m.id));
  const isNew = (m) => {
    const ca = m.resource?.createdAt ? new Date(m.resource.createdAt).getTime() : 0;
    return ca >= Date.now() - 1000 * 60 * 60 * 24; // taken within last 24h
  };

  return (
    <Paper variant="outlined" sx={{ p: dense ? 1.5 : 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
          Equipment Photos {value.length > 0 ? `(${value.length} selected)` : ''}
        </Typography>
        {ccProjectUrl && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            component="a"
            href={ccProjectUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open CompanyCam
          </Button>
        )}
        <Button
          size="small"
          variant="contained"
          startIcon={<AddAPhotoIcon />}
          onClick={() => setPickerOpen(true)}
          disabled={!propertyId}
        >
          Select photos
        </Button>
      </Box>

      <Alert severity="info" icon={false} sx={{ py: 0.5, mb: selectedItems.length ? 1 : 0 }}>
        <Typography variant="caption" sx={{ display: 'block' }}>
          You can only pick photos that are <strong>already uploaded to this property</strong> (synced from CompanyCam).
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
          <strong>Haven't taken the label photo (model + serial) yet?</strong> Create the
          ticket first, then take the photo in CompanyCam and add it from the ticket's
          <em> Edit</em> mode → <em>Equipment Photos → Attach photos</em>.
        </Typography>
      </Alert>

      {selectedItems.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(84px, 1fr))',
            gap: 1,
          }}
        >
          {selectedItems.map((m) => {
            const url = m.resource?.url;
            if (!url) return null;
            return (
              <Box
                key={m.id}
                sx={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '1 / 1',
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  cursor: 'pointer',
                  '&:hover .pick-rm': { opacity: 1 },
                }}
                onClick={() => setLightboxUrl(url)}
              >
                <img src={url} alt={m.name || 'photo'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <Box className="pick-rm" sx={{ position: 'absolute', top: 2, right: 2, opacity: 0.85 }}>
                  <Tooltip title="Remove">
                    <IconButton
                      size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'white' } }}
                      onClick={(e) => { e.stopPropagation(); removeSelected(m.id); }}
                    >
                      <CloseIcon fontSize="small" color="error" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* ---------- Picker dialog ---------- */}
      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>Select equipment photos</Typography>
          <Button size="small" startIcon={pulling ? <CircularProgress size={14} /> : <RefreshIcon />} onClick={refresh} disabled={pulling}>
            Refresh
          </Button>
        </DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : items.length === 0 ? (
            <Alert severity="info">
              This property has no photos yet. Take a photo of the equipment label in
              CompanyCam, then tap "Refresh". If it still doesn't show up, just create
              the ticket and add the photo later from the ticket's Edit mode → Equipment Photos.
            </Alert>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Tap photos to select. Recent photos are marked NEW.
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 1 }}>
                {items.map((m) => {
                  const url = m.resource?.url;
                  if (!url) return null;
                  const isSel = selected.has(m.id);
                  return (
                    <Box
                      key={m.id}
                      onClick={() => togglePick(m.id)}
                      sx={{
                        position: 'relative',
                        width: '100%',
                        aspectRatio: '1 / 1',
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: '2px solid',
                        borderColor: isSel ? 'primary.main' : 'divider',
                        cursor: 'pointer',
                      }}
                    >
                      <img src={url} alt={m.name || 'photo'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <Box sx={{ position: 'absolute', top: 4, left: 4, bgcolor: 'rgba(255,255,255,0.85)', borderRadius: '50%', display: 'flex', width: 26, height: 26, alignItems: 'center', justifyContent: 'center' }}>
                        {isSel ? <CheckCircleIcon fontSize="small" color="primary" /> : <RadioButtonUncheckedIcon fontSize="small" />}
                      </Box>
                      {isNew(m) && !isSel && (
                        <Chip size="small" label="NEW" color="warning" sx={{ position: 'absolute', bottom: 4, left: 4, height: 18, fontSize: '0.65rem', fontWeight: 700 }} />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)}>Done {value.length > 0 ? `(${value.length})` : ''}</Button>
        </DialogActions>
      </Dialog>

      {/* ---------- Lightbox ---------- */}
      <Dialog open={!!lightboxUrl} onClose={() => setLightboxUrl(null)} maxWidth="lg" fullWidth PaperProps={{ sx: { bgcolor: '#000' } }}>
        <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <IconButton onClick={() => setLightboxUrl(null)} sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }, zIndex: 1 }}>
            <CloseIcon />
          </IconButton>
          {lightboxUrl && (
            <img src={lightboxUrl} alt="full size" style={{ maxWidth: '100%', maxHeight: '90vh', display: 'block', objectFit: 'contain' }} />
          )}
        </Box>
      </Dialog>
    </Paper>
  );
}
